import React, { useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react';

export type ConfirmVariant = 'danger' | 'warning' | 'info';

interface ConfirmDialogWithOptionsProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  alternativeLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  onConfirm: () => void;
  onAlternative?: () => void;
  onCancel: () => void;
}

export const ConfirmDialogWithOptions: React.FC<ConfirmDialogWithOptionsProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmer',
  alternativeLabel,
  cancelLabel = 'Annuler',
  variant = 'info',
  onConfirm,
  onAlternative,
  onCancel,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const config = {
    danger: {
      icon: AlertTriangle,
      iconColor: 'tw-text-red-600',
      iconBg: 'tw-bg-red-100',
      confirmBg: 'tw-bg-red-600 hover:tw-bg-red-700 focus:tw-ring-red-500',
    },
    warning: {
      icon: AlertCircle,
      iconColor: 'tw-text-yellow-600',
      iconBg: 'tw-bg-yellow-100',
      confirmBg: 'tw-bg-yellow-600 hover:tw-bg-yellow-700 focus:tw-ring-yellow-500',
    },
    info: {
      icon: Info,
      iconColor: 'tw-text-affinicar-primary',
      iconBg: 'tw-bg-affinicar-lighter',
      confirmBg: 'tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary focus:tw-ring-affinicar-primary',
    },
  };

  const { icon: Icon, iconColor, iconBg, confirmBg } = config[variant];

  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-50 tw-transition-opacity"
        onClick={onCancel}
      />

      <div className="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto">
        <div className="tw-flex tw-min-h-full tw-items-center tw-justify-center tw-p-4">
          <div
            className="tw-relative tw-w-full tw-max-w-md tw-bg-white tw-rounded-lg tw-shadow-xl tw-transform tw-transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onCancel}
              className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
              aria-label="Fermer"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>

            <div className="tw-p-6">
              <div className="tw-flex tw-items-start tw-gap-4">
                <div className={`tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center ${iconBg}`}>
                  <Icon className={`tw-w-6 tw-h-6 ${iconColor}`} />
                </div>
                <div className="tw-flex-1 tw-pt-1">
                  <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                    {title}
                  </h3>
                  <p className="tw-text-sm tw-text-gray-600 tw-whitespace-pre-line">
                    {message}
                  </p>
                </div>
              </div>

              <div className="tw-flex tw-justify-end tw-gap-3 tw-mt-6">
                <button
                  type="button"
                  onClick={onCancel}
                  className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-affinicar-primary"
                >
                  {cancelLabel}
                </button>
                {alternativeLabel && onAlternative && (
                  <button
                    type="button"
                    onClick={onAlternative}
                    className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-gray-500"
                  >
                    {alternativeLabel}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onConfirm}
                  className={`tw-px-4 tw-py-2 tw-border tw-border-transparent tw-rounded-md tw-shadow-sm tw-text-sm tw-font-medium tw-text-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 ${confirmBg}`}
                  autoFocus
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
